<template>
  <div class="layout" :class="{ 'layout--not-found': isNotFound }">
    <header class="layout__header">
      <div class="header__container">
        <div class="brand">
          <img
            class="brand__logo"
            src="@assets/logo.png"
            :alt="logoAlt"
          />
          <div>
            <h1>{{ brand.title }}</h1>
            <p>{{ brand.tagline }}</p>
          </div>
        </div>
        <div class="header__actions">
          <nav class="nav">
            <router-link
              v-for="item in navLinks"
              :key="item.to"
              :to="item.to"
              exact
            >
              {{ item.label }}
            </router-link>
          </nav>
          <div class="language" role="group" :aria-label="languageLabel">
            <button
              v-for="option in languageOptions"
              :key="option.value"
              type="button"
              class="language__button"
              :class="{ 'language__button--active': option.value === currentLocale }"
              @click="handleLocaleChange(option.value)"
            >
              {{ option.label }}
            </button>
          </div>
        </div>
        <button
          type="button"
          class="menu-toggle"
          :class="{ 'menu-toggle--open': isMobileMenuOpen }"
          :aria-expanded="isMobileMenuOpen.toString()"
          :aria-label="isMobileMenuOpen ? closeMenuLabel : openMenuLabel"
          @click="toggleMobileMenu"
        >
          <span></span>
          <span></span>
          <span></span>
        </button>
      </div>
    </header>
    <transition name="mobile-nav">
      <div
        v-if="isMobileMenuOpen"
        class="mobile-nav"
        role="dialog"
        aria-modal="true"
        :aria-label="mobileNavLabel"
      >
        <div class="mobile-nav__panel">
          <div class="mobile-nav__top">
            <div class="mobile-nav__brand">
              <p>{{ brand.tagline }}</p>
              <h2>{{ brand.title }}</h2>
            </div>
            <button
              type="button"
              class="mobile-nav__close"
              @click="closeMobileMenu"
              :aria-label="closeMenuLabel"
            >
              ×
            </button>
          </div>
          <nav class="mobile-nav__links">
            <router-link
              v-for="item in navLinks"
              :key="item.to"
              :to="item.to"
              exact
              @click.native="handleMobileNavClick"
            >
              {{ item.label }}
            </router-link>
          </nav>
          <div class="mobile-nav__language" role="group" :aria-label="languageLabel">
            <button
              v-for="option in languageOptions"
              :key="option.value"
              type="button"
              class="language__button"
              :class="{ 'language__button--active': option.value === currentLocale }"
              @click="handleLocaleChange(option.value)"
            >
              {{ option.label }}
            </button>
          </div>
        </div>
      </div>
    </transition>
    <main class="layout__main">
      <slot />
    </main>
    <footer class="layout__footer">
      <small>{{ footerCopy }}</small>
    </footer>
  </div>
</template>

<script>
import { setLocale } from '@/i18n';
import { getLocaleBlock } from '@/utils/i18n';

export default {
  name: 'MainLayout',
  data() {
    return {
      isMobileMenuOpen: false,
    };
  },
  computed: {
    isNotFound() {
      return this.$route.name === 'NotFound';
    },
    currentLocale() {
      return this.$i18n.locale;
    },
    layoutContent() {
      return getLocaleBlock(this.$i18n, 'layout', {});
    },
    brand() {
      return this.layoutContent?.brand || {};
    },
    navLinks() {
      return this.layoutContent?.nav || [];
    },
    languageOptions() {
      const options = this.layoutContent?.language?.options || {};
      return Object.keys(options).map((value) => ({
        value,
        label: options[value],
      }));
    },
    languageLabel() {
      return this.layoutContent?.language?.label || '';
    },
    footerCopy() {
      const template = this.layoutContent?.footer || '';
      return template.replace('{year}', new Date().getFullYear());
    },
    logoAlt() {
      return this.layoutContent?.logoAlt || this.brand?.title || 'Lab logo';
    },
    mobileMenuContent() {
      return this.layoutContent?.mobileMenu || {};
    },
    openMenuLabel() {
      return this.mobileMenuContent.open || 'Open navigation';
    },
    closeMenuLabel() {
      return this.mobileMenuContent.close || 'Close navigation';
    },
    mobileNavLabel() {
      return this.mobileMenuContent.label || 'Primary navigation';
    },
  },
  watch: {
    $route() {
      this.isMobileMenuOpen = false;
    },
  },
  methods: {
    handleLocaleChange(locale) {
      setLocale(locale);
    },
    toggleMobileMenu() {
      this.isMobileMenuOpen = !this.isMobileMenuOpen;
    },
    closeMobileMenu() {
      this.isMobileMenuOpen = false;
    },
    handleMobileNavClick() {
      this.closeMobileMenu();
    },
  },
};
</script>

<style scoped>
.layout {
  display: flex;
  min-height: 100vh;
  flex-direction: column;
  background-color: var(--background-color);
}
/* The fancy gradient for the 404 page is a nice touch, let's keep it but tone it down slightly with the new palette */
.layout--not-found {
  background: radial-gradient(circle at 30% 20%, var(--primary-color-light), transparent 40%),
    radial-gradient(circle at 75% 40%, rgba(255, 210, 115, 0.1), transparent 35%),
    linear-gradient(135deg, #f2f5fa 0%, var(--background-color) 45%, #f7f9fd 100%);
}

.layout--not-found .layout__footer {
  background: transparent;
}
.layout__header {
  position: sticky;
  top: 0;
  z-index: 10;
  /* A dark header provides good contrast and a premium feel */
  background: rgba(18, 24, 38, 0.85);
  backdrop-filter: blur(16px);
  color: var(--text-color-on-primary);
  box-shadow: var(--box-shadow-lg);
  border-bottom: var(--border-width) solid rgba(255, 255, 255, 0.1);
}

.header__container {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: var(--spacing-sm) 3vw;
  gap: var(--spacing-lg);
}

.brand {
  display: flex;
  align-items: center;
  gap: var(--spacing-md);
}

.brand__logo {
    width: 184px;
    height: 59px;
  filter: drop-shadow(0 4px 8px rgba(0, 0, 0, 0.2));
}

.brand h1 {
  font-size: var(--font-size-lg);
  margin: 0;
  font-weight: var(--font-weight-bold);
}

.brand p {
  margin: var(--spacing-xs) 0 0;
  font-size: var(--font-size-sm);
  opacity: 0.85;
}

.header__actions {
  display: flex;
  align-items: center;
  gap: var(--spacing-lg);
}

.menu-toggle {
  display: none;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  width: 2.75rem;
  height: 2.75rem;
  border-radius: 50%;
  border: 1px solid rgba(255, 255, 255, 0.35);
  background: linear-gradient(145deg, rgba(255, 255, 255, 0.08), rgba(255, 255, 255, 0.02));
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.25), inset 0 1px 0 rgba(255, 255, 255, 0.12);
  cursor: pointer;
  gap: 0.3rem;
  color: rgba(255, 255, 255, 0.92);
  transition: border-color 0.25s ease, background 0.25s ease, box-shadow 0.25s ease;
}

.menu-toggle span {
  display: block;
  width: 55%;
  height: 2px;
  background: currentColor;
  border-radius: 999px;
  transition: transform 0.25s ease, opacity 0.2s ease, width 0.2s ease;
  box-shadow: 0 0 1px rgba(255, 255, 255, 0.35);
}

.menu-toggle:hover {
  border-color: var(--text-color-on-primary);
  background: rgba(255, 255, 255, 0.12);
  box-shadow: 0 12px 28px rgba(0, 0, 0, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.2);
}

.menu-toggle--open span:nth-child(1) {
  transform: translateY(6px) rotate(45deg);
}

.menu-toggle--open span:nth-child(2) {
  opacity: 0;
  transform: translateX(6px);
  width: 40%;
}

.menu-toggle--open span:nth-child(3) {
  transform: translateY(-6px) rotate(-45deg);
}

.nav {
  display: flex;
  gap: var(--spacing-lg);
  font-weight: var(--font-weight-bold);
}

.nav a {
  color: inherit;
  text-decoration: none;
  letter-spacing: 0.01em;
  padding: var(--spacing-sm) 0;
  border-bottom: 2px solid transparent;
  transition: opacity 0.2s ease, border-color 0.2s ease;
}

.nav a.router-link-exact-active {
  border-color: var(--primary-color);
  color: var(--primary-color-light);
}

.nav a:hover:not(.router-link-exact-active) {
  opacity: 0.8;
  border-bottom-color: rgba(255, 255, 255, 0.5);
}

.language {
  display: inline-flex;
  align-items: center;
  gap: var(--spacing-xs);
  background: rgba(0, 0, 0, 0.2);
  border-radius: 999px;
  padding: var(--spacing-xs);
}

.language__button {
  border: none;
  background: transparent;
  color: var(--text-color-on-primary);
  font-weight: var(--font-weight-bold);
  padding: var(--spacing-xs) var(--spacing-sm);
  border-radius: 999px;
  cursor: pointer;
  transition: background 0.2s ease, color 0.2s ease;
}

.language__button--active {
  background: var(--primary-color);
  color: var(--text-color-on-primary);
}

.language__button:not(.language__button--active):hover {
  background: rgba(255, 255, 255, 0.15);
}

.layout__main {
  flex: 1;
  padding: var(--spacing-xl) 3vw;
}

.layout__footer {
  padding: var(--spacing-lg) 3vw;
  text-align: center;
  color: var(--text-color-subtle);
  background-color: transparent;
  border-top: none;
}

.mobile-nav {
  position: fixed;
  inset: 0;
  background: rgba(10, 14, 21, 0.7);
  backdrop-filter: blur(8px);
  display: flex;
  justify-content: center;
  align-items: center;
  padding: var(--spacing-md);
  z-index: 20;
}

.mobile-nav__panel {
  width: min(480px, 94vw);
  background: var(--surface-color);
  color: var(--text-color);
  border-radius: var(--border-radius);
  padding: var(--spacing-lg) var(--spacing-md);
  box-shadow: var(--box-shadow-lg);
  display: flex;
  flex-direction: column;
  gap: var(--spacing-lg);
}

.mobile-nav__top {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: var(--spacing-md);
  padding: 0 var(--spacing-sm);
}

.mobile-nav__brand p {
  margin: 0;
  font-size: var(--font-size-sm);
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: var(--text-color-subtle);
}

.mobile-nav__brand h2 {
  margin: var(--spacing-xs) 0 0;
  font-size: var(--font-size-xl);
  line-height: 1.2;
}

.mobile-nav__close {
  font-size: 1.8rem;
  line-height: 1;
  border: none;
  background: transparent;
  cursor: pointer;
  color: var(--text-color-light);
  padding: 0.15rem;
  transition: color 0.2s ease;
}

.mobile-nav__close:hover {
  color: var(--text-color);
}

.mobile-nav__links {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-sm);
}

.mobile-nav__links a {
  text-decoration: none;
  color: var(--text-color-light);
  font-size: var(--font-size-lg);
  font-weight: var(--font-weight-bold);
  padding: var(--spacing-sm) 0;
  text-align: center;
  border-radius: var(--border-radius);
  transition: background 0.2s ease, color 0.2s ease;
}

.mobile-nav__links a.router-link-exact-active {
  background: var(--primary-color-light);
  color: var(--primary-color-dark);
}

.mobile-nav__links a:hover:not(.router-link-exact-active) {
  background: var(--background-color);
  color: var(--text-color);
}

.mobile-nav__language {
  display: flex;
  justify-content: center;
  gap: var(--spacing-sm);
  flex-wrap: wrap;
}

.mobile-nav .language__button {
  color: var(--text-color);
}

.mobile-nav .language__button:not(.language__button--active):hover {
  background: var(--background-color);
}

.mobile-nav-enter-active,
.mobile-nav-leave-active {
  transition: opacity 0.25s ease;
}

.mobile-nav-enter,
.mobile-nav-leave-to {
  opacity: 0;
}

@media (max-width: 900px) {
  .header__container {
    flex-direction: column;
    align-items: flex-start;
    gap: var(--spacing-md);
  }

  .header__actions {
    width: 100%;
    justify-content: space-between;
  }

  .nav {
    flex-wrap: wrap;
    gap: var(--spacing-md);
  }
}

@media (max-width: 640px) {
  .header__container {
    flex-direction: row;
    align-items: center;
    padding: var(--spacing-sm) 5vw;
    gap: var(--spacing-sm);
  }

  .brand {
    flex: 1;
    width: auto;
    gap: var(--spacing-sm);
  }

  .brand__logo {
    width: 128px;
    height: auto;
  }

  .header__actions {
    display: none;
  }

  .brand h1 {
    font-size: var(--font-size-base);
  }

  .brand p {
    font-size: 0.8rem;
  }

  .menu-toggle {
    display: flex;
  }
}
</style>
